/**
 * Utility for handling marker overlapping on maps
 * Offsets markers that are too close together to avoid visual overlap
 */

const OVERLAP_DISTANCE = 25; // pixels - if markers are closer than this, offset them

export function getMarkerOffset(markerIndex, totalMarkersAtPoint, radius = 45) {
  if (totalMarkersAtPoint <= 1) return { offsetX: 0, offsetY: 0 };

  const anglePerMarker = (Math.PI * 2) / totalMarkersAtPoint;
  const angle = anglePerMarker * markerIndex;

  return {
    offsetX: Math.cos(angle) * radius,
    offsetY: Math.sin(angle) * radius,
  };
}

export function detectOverlappingMarkers(markers) {
  if (!Array.isArray(markers) || markers.length === 0) return [];

  // Group markers by proximity
  const groups = [];

  markers.forEach((marker, idx) => {
    let foundGroup = false;

    for (let group of groups) {
      const representative = group[0];
      const dist = Math.hypot(
        (marker.x || 0) - (representative.x || 0),
        (marker.y || 0) - (representative.y || 0)
      );

      if (dist < OVERLAP_DISTANCE) {
        group.push({ ...marker, originalIndex: idx });
        foundGroup = true;
        break;
      }
    }

    if (!foundGroup) {
      groups.push([{ ...marker, originalIndex: idx }]);
    }
  });

  return groups;
}

export function applyMarkerOffsets(markers) {
  const groups = detectOverlappingMarkers(markers);

  return markers.map((marker) => {
    // Find which group this marker belongs to
    let groupIndex = 0;
    let indexInGroup = 0;

    for (let groupIdx = 0; groupIdx < groups.length; groupIdx++) {
      const markerIndexInGroup = groups[groupIdx].findIndex(
        (m) => m.originalIndex === markers.indexOf(marker)
      );

      if (markerIndexInGroup !== -1) {
        groupIndex = groupIdx;
        indexInGroup = markerIndexInGroup;
        break;
      }
    }

    const group = groups[groupIndex];

    if (group.length <= 1) {
      return marker; // No offset needed
    }

    const offset = getMarkerOffset(indexInGroup, group.length, 40);

    return {
      ...marker,
      displayX: (marker.x || 0) + offset.offsetX,
      displayY: (marker.y || 0) + offset.offsetY,
      isOffsetted: true,
      groupSize: group.length,
    };
  });
}

/**
 * Alternative: Create a single cluster marker for overlapping items
 */
export function createClusterMarkers(markers) {
  const groups = detectOverlappingMarkers(markers);
  const clusterMarkers = [];
  const clusteredMarkerIds = new Set();

  groups.forEach((group, groupIdx) => {
    if (group.length === 1) {
      clusterMarkers.push(group[0]);
    } else {
      // Create a cluster marker
      const representative = group[0];
      clusterMarkers.push({
        ...representative,
        id: `cluster-${groupIdx}`,
        type: "cluster",
        clusterCount: group.length,
        clusterMembers: group,
        x: representative.x,
        y: representative.y,
      });

      group.forEach((m) => clusteredMarkerIds.add(m.id));
    }
  });

  return clusterMarkers;
}
